import React, { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { confirmImport, errorMessage, previewImport } from './libraryApi';
import { formatServiceDate, pluralize } from './libraryFormat';

const STATE_LABELS = { new: 'Nieuw', already_imported: 'Al geïmporteerd', duplicate_content: 'Mogelijk duplicaat' };

const FileField = ({ label, hint, file, onChange }) => (
  <label className="block space-y-1 text-sm">
    <span className="font-medium text-slate-900">{label}</span>
    <input type="file" accept=".csv,text/csv" aria-label={label} className="block min-h-11 w-full text-sm file:mr-3 file:min-h-11 file:rounded-md file:border file:border-slate-200 file:bg-white file:px-3"
      onChange={(event) => onChange(event.target.files?.[0] || null)} />
    <span className="block text-xs text-slate-500">{file ? file.name : hint}</span>
  </label>
);

const LineChoice = ({ record, item, choice, onChoose }) => {
  if (item.match_status === 'exact') return <span className="text-xs text-emerald-800">Gekoppeld aan {item.song_title}</span>;
  if (item.match_status === 'none') return <span className="text-xs text-slate-500">Geen match — blijft met de oorspronkelijke titel staan</span>;
  const name = `${record.source_key}-${item.order}`;
  return (
    <fieldset className="space-y-1 text-xs">
      <legend className="text-amber-800">Mogelijke match ({item.match_reason?.toLowerCase()}) — bevestig of laat leeg:</legend>
      {item.candidates.map((candidate) => (
        <label key={candidate.song_id} className="flex min-h-11 items-center gap-2">
          <input type="radio" name={name} checked={choice === candidate.song_id} onChange={() => onChoose(candidate.song_id)} aria-label={`Koppel ${item.original_title} aan ${candidate.title}`} />
          {candidate.title}
        </label>
      ))}
      <label className="flex min-h-11 items-center gap-2"><input type="radio" name={name} checked={!choice} onChange={() => onChoose(null)} />Niet koppelen</label>
    </fieldset>
  );
};

/** Upload -> check -> confirm. Nothing is stored until the user confirms the preview. */
const ImportWizard = ({ open, onOpenChange, onImported }) => {
  const [servicesFile, setServicesFile] = useState(null);
  const [linesFile, setLinesFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [include, setInclude] = useState({});
  const [choices, setChoices] = useState({});
  const [error, setError] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setPreview(null); setResult(null); setError([]); setServicesFile(null); setLinesFile(null); setInclude({}); setChoices({}); };
  const close = (value) => { if (!value) reset(); onOpenChange(value); };

  const upload = async () => {
    setBusy(true); setError([]);
    try {
      const data = await previewImport(servicesFile, linesFile);
      setPreview(data);
      setInclude(Object.fromEntries(data.records.map((record) => [record.source_key, record.include_default])));
      setChoices({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail?.errors?.length ? detail.errors : [errorMessage(err, 'Uploaden mislukt')]);
    } finally { setBusy(false); }
  };

  const choose = (record, item, songId) => setChoices((current) => ({ ...current, [`${record.source_key}::${item.order}`]: songId }));
  const acceptAllSuggestions = () => {
    const next = {};
    preview.records.filter((record) => include[record.source_key]).forEach((record) => record.items.forEach((item) => {
      if (item.match_status === 'possible' && item.candidates.length) next[`${record.source_key}::${item.order}`] = item.candidates[0].song_id;
    }));
    setChoices(next);
  };

  const save = async () => {
    setBusy(true); setError([]);
    const selected = preview.records.filter((record) => include[record.source_key]).map((record) => record.source_key);
    const links = Object.entries(choices).filter(([key]) => selected.includes(key.split('::')[0])).map(([key, songId]) => {
      const [source_key, order] = key.split('::');
      return { source_key, order: Number(order), song_id: songId };
    });
    try {
      const data = await confirmImport(preview.batch_id, { include_source_keys: selected, links, group_overrides: {} });
      setResult(data);
      toast.success(`${pluralize(data.imported, 'setlist', 'setlists')} geïmporteerd`);
      onImported(data);
    } catch (err) {
      setError([errorMessage(err, 'Importeren mislukt')]);
    } finally { setBusy(false); }
  };

  const selectedCount = preview ? preview.records.filter((record) => include[record.source_key]).length : 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Historische setlists importeren</DialogTitle>
          <DialogDescription>Controleer eerst het voorbeeld. Er wordt niets opgeslagen en geen bestaande planning gewijzigd voordat je bevestigt.</DialogDescription>
        </DialogHeader>

        {error.length > 0 && <ul role="alert" className="list-disc space-y-1 rounded-md border border-red-200 bg-red-50 p-3 pl-7 text-sm text-red-800">{error.map((message) => <li key={message}>{message}</li>)}</ul>}

        {result ? (
          <div className="space-y-3 text-sm">
            <p className="font-medium text-slate-900">{pluralize(result.imported, 'setlist', 'setlists')} opgeslagen als concept.</p>
            {result.skipped_existing > 0 && <p>{pluralize(result.skipped_existing, 'setlist was', 'setlists waren')} al aanwezig en is niet opnieuw opgeslagen.</p>}
            <Button type="button" className="min-h-11" onClick={() => close(false)}>Sluiten</Button>
          </div>
        ) : !preview ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload de twee CSV-bestanden (UTF-8, komma of puntkomma). Andere kolommen, zoals namen of telefoonnummers, worden genegeerd en niet opgeslagen.</p>
            <FileField label="Zangdiensten (zoea_zangdiensten.csv)" hint="Kolommen: service_id, datum, gelegenheid, versie (optioneel)" file={servicesFile} onChange={setServicesFile} />
            <FileField label="Liedregels (zoea_liedregels.csv)" hint="Kolommen: service_id, volgorde, titel, toonsoort, opwekking, opmerking" file={linesFile} onChange={setLinesFile} />
            <Button type="button" className="min-h-11 bg-teal-600 text-white hover:bg-teal-700" disabled={!servicesFile || !linesFile || busy} onClick={upload}>{busy ? 'Bezig…' : 'Voorbeeld maken'}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              {[['Setlists', preview.summary.services], ['Liedregels', preview.summary.lines], ['Nieuw', preview.summary.new], ['Mogelijke duplicaten', preview.summary.already_imported + preview.summary.duplicate_content],
                ['Exacte matches', preview.summary.matches.exact], ['Voorstellen', preview.summary.matches.possible], ['Geen match', preview.summary.matches.none], ['Zonder datum', preview.summary.undated]].map(([label, value]) => (
                <div key={label} className="rounded-md border border-slate-200 p-2"><dt className="text-xs text-slate-500">{label}</dt><dd className="text-lg font-semibold">{value}</dd></div>
              ))}
            </dl>
            {preview.warnings.length > 0 && <details className="text-sm"><summary className="min-h-11 cursor-pointer text-amber-800">{pluralize(preview.warnings.length, 'waarschuwing', 'waarschuwingen')}</summary><ul className="list-disc pl-5">{preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></details>}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="min-h-11" onClick={acceptAllSuggestions}>Bevestig alle eerste voorstellen</Button>
              <Button type="button" variant="outline" className="min-h-11" onClick={() => setChoices({})}>Wis keuzes</Button>
            </div>
            <ul className="space-y-2">
              {preview.records.map((record) => (
                <li key={record.source_key} className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1 h-5 w-5" checked={!!include[record.source_key]} aria-label={`Importeer ${record.occasion || record.source_key}`}
                      onChange={(event) => setInclude({ ...include, [record.source_key]: event.target.checked })} />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="font-medium text-slate-900">{formatServiceDate(record.service_date, record.date_certain)} · {record.occasion || 'Zangdienst'}{record.version_label ? ` · ${record.version_label}` : ''}</p>
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        <Badge variant="secondary">{STATE_LABELS[record.state]}</Badge>
                        <Badge variant="secondary">{pluralize(record.item_count, 'lied', 'liederen')}</Badge>
                        {record.group_reason && <Badge className="bg-amber-50 text-amber-800">{record.group_joins_existing ? 'Mogelijk nieuwe versie van een bestaande setlist' : 'Mogelijk versie van dezelfde dienst'}{record.group_reason === 'overlap_onzekere_datum' ? ' (datum onzeker)' : ''}</Badge>}
                        {record.same_date_other_list && <Badge variant="secondary">Andere lijst op dezelfde datum</Badge>}
                      </div>
                      <details className="text-sm">
                        <summary className="min-h-11 cursor-pointer py-2 text-teal-800">Liederen bekijken en koppelen</summary>
                        <ol className="space-y-2">
                          {record.items.map((item) => (
                            <li key={item.order} className="rounded bg-slate-50 p-2">
                              <p className="break-words">{item.order}. {item.original_title}</p>
                              <LineChoice record={record} item={item} choice={choices[`${record.source_key}::${item.order}`]} onChoose={(songId) => choose(record, item, songId)} />
                            </li>
                          ))}
                        </ol>
                      </details>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-slate-200 bg-white py-3">
              <Button type="button" className="min-h-11 bg-teal-600 text-white hover:bg-teal-700" disabled={busy || selectedCount === 0} onClick={save}>{busy ? 'Bezig…' : `${pluralize(selectedCount, 'setlist', 'setlists')} importeren`}</Button>
              <Button type="button" variant="outline" className="min-h-11" onClick={reset}>Andere bestanden</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportWizard;
